import type { ComponentProps } from 'react';

import type { CtaSurfaceTone } from '@/components/apps/ui/cta/CtaActionButton';
import type SectionTitle from '@/components/apps/ui/title/SectionTitle';

/**
 * ### SolutionSurfaceTone
 *
 * Background tone of the surface a shared solution section sits on. `dark` is
 * the near-black surface of the solution pages; `light` is the white surface
 * of the home page. Each shared section maps the tone to its own text, divider,
 * and control colors, so a caller names the surface once instead of picking
 * individual colors. Same vocabulary as the call-to-action button's tone, so a
 * section hands its own tone straight to the button it renders.
 *
 * @see {@link CtaSurfaceTone} The call-to-action button's tone this aliases
 */
export type SolutionSurfaceTone = CtaSurfaceTone;

type SectionTitleColor = NonNullable<
  ComponentProps<typeof SectionTitle>['color']
>;

/**
 * ### SECTION_TITLE_COLOR_BY_TONE
 *
 * Resolves a surface tone to the `color` prop SectionTitle expects. SectionTitle
 * names its text color rather than the surface, so a dark surface takes the
 * lighter text and a light surface the darker text.
 */
export const SECTION_TITLE_COLOR_BY_TONE: Record<
  SolutionSurfaceTone,
  SectionTitleColor
> = {
  dark: 'lighter',
  light: 'darker',
};

/**
 * ### SURFACE_BACKGROUND_CLASS_BY_TONE
 *
 * Background utility a shared section paints on its own `section` element.
 * Sections own their background rather than inheriting it so the same section
 * reads identically whichever page places it.
 */
export const SURFACE_BACKGROUND_CLASS_BY_TONE: Record<
  SolutionSurfaceTone,
  string
> = {
  dark: 'bg-apps-brandGrayBlack',
  light: 'bg-apps-coreGrayWhite',
};
